import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {components} from '@/lib/api-schema'

export type PlanEstado = components['schemas']['PlanEstadoRead']
export type PlanTrabajo = components['schemas']['PlanTrabajoRead']
export type PlanActividad = components['schemas']['ActividadRead']
export type Proponer = components['schemas']['ProponerRead']
export type ActividadPropuesta = components['schemas']['ActividadPropuesta']
export type ActividadCrear = components['schemas']['ActividadCrear']

export function usePlanTrabajo(businessId: string | undefined) {
  return useQuery({
    queryKey: ['plan-trabajo', businessId],
    queryFn: async () => (await api.get<PlanEstado>(`/businesses/${businessId}/sgsst/plan-trabajo`)).data,
    enabled: !!businessId,
  })
}

export function useProponerPlan(businessId: string) {
  return useMutation({
    mutationFn: async (tieneePlanFirmado: boolean) =>
      (
        await api.post<Proponer>(`/businesses/${businessId}/sgsst/plan-trabajo/proponer`, {
          tiene_plan_firmado: tieneePlanFirmado,
        })
      ).data,
  })
}

function invalidate(queryClient: ReturnType<typeof useQueryClient>, businessId: string) {
  queryClient.invalidateQueries({queryKey: ['plan-trabajo', businessId]})
  queryClient.invalidateQueries({queryKey: ['ruta', businessId]})
  queryClient.invalidateQueries({queryKey: ['capabilities', businessId]})
}

export function useCrearPlan(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (actividades: ActividadCrear[]) =>
      (await api.post<PlanTrabajo>(`/businesses/${businessId}/sgsst/plan-trabajo`, {actividades})).data,
    onSuccess: () => invalidate(queryClient, businessId),
  })
}

export function useQuitarActividad(businessId: string, planId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (actividadId: string) =>
      (
        await api.delete<PlanTrabajo>(
          `/businesses/${businessId}/sgsst/plan-trabajo/${planId}/actividades/${actividadId}`
        )
      ).data,
    onSuccess: () => invalidate(queryClient, businessId),
  })
}

export function useMarcarEjecucion(businessId: string, planId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({actividadId, mes, fecha}: {actividadId: string; mes: number; fecha: string}) =>
      (
        await api.post<PlanTrabajo>(
          `/businesses/${businessId}/sgsst/plan-trabajo/${planId}/actividades/${actividadId}/ejecucion`,
          {mes, fecha}
        )
      ).data,
    onSuccess: () => invalidate(queryClient, businessId),
  })
}

export function useDesmarcarEjecucion(businessId: string, planId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({actividadId, mes}: {actividadId: string; mes: number}) =>
      (
        await api.delete<PlanTrabajo>(
          `/businesses/${businessId}/sgsst/plan-trabajo/${planId}/actividades/${actividadId}/ejecucion/${mes}`
        )
      ).data,
    onSuccess: () => invalidate(queryClient, businessId),
  })
}

export type EncabezadoUpdate = components['schemas']['EncabezadoUpdate']

export function useEditarEncabezado(businessId: string, planId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: EncabezadoUpdate) =>
      (await api.patch<PlanTrabajo>(`/businesses/${businessId}/sgsst/plan-trabajo/${planId}`, payload)).data,
    onSuccess: () => invalidate(queryClient, businessId),
  })
}

export type ActividadUpdate = components['schemas']['ActividadUpdate']

export function useEditarActividad(businessId: string, planId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({actividadId, payload}: {actividadId: string; payload: ActividadUpdate}) =>
      (
        await api.patch<PlanTrabajo>(
          `/businesses/${businessId}/sgsst/plan-trabajo/${planId}/actividades/${actividadId}`,
          payload
        )
      ).data,
    onSuccess: () => invalidate(queryClient, businessId),
  })
}

export function useFirmarPlan(businessId: string, planId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (rol: 'responsable' | 'representante') =>
      (await api.post<PlanTrabajo>(`/businesses/${businessId}/sgsst/plan-trabajo/${planId}/firmar`, {rol})).data,
    onSuccess: () => invalidate(queryClient, businessId),
  })
}

export function useCrearAdenda(businessId: string, planId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () =>
      (await api.post<PlanTrabajo>(`/businesses/${businessId}/sgsst/plan-trabajo/${planId}/adenda`, {})).data,
    onSuccess: () => invalidate(queryClient, businessId),
  })
}
